/**
 * Client refunds (KAN-40 / KAN-58).
 *
 * A refund is money going *back* to a client, and it is the only movement in
 * this platform that is neither a receipt nor a disbursement to a supplier. It
 * gets its own vocabulary because two facts about it live in different places:
 *
 *  - **That it happened** is owned upstream. A trust refund is a withdrawal
 *    from the IOLTA account in Clio, and no amount of local record-keeping
 *    makes it true or untrue.
 *  - **That somebody approved it** is owned *here*. Neither Clio nor QuickBooks
 *    records an approver, an approval state, or a reason drawn from a list —
 *    the nearest thing either has is free text on a bank transaction.
 *
 * That split is the whole design. A refund row is keyed by provenance when
 * something upstream owns it, carries local approval state either way, and is
 * the one table in this codebase where a row can legitimately start life with
 * no upstream record at all — because the point of recording a refund before it
 * is processed is to have it approved first.
 *
 * Everything here is shared with the SPA so the labels a person reads and the
 * rules the API applies cannot drift apart.
 */
/**
 * The lifecycle, and every state has exactly one writer.
 *
 * There is deliberately no `processing` or `failed`. Both would be states this
 * platform asserts about work happening in another system, and neither Clio nor
 * QuickBooks tells us when a refund is mid-flight — the row would sit in a
 * status nothing could ever move it out of. The evidence that a refund left is
 * an upstream record of it leaving, so `completed` has one writer and it is the
 * sync.
 *
 *  - `pending_approval` — recorded here. Written by a person. No money moved.
 *  - `approved`         — cleared to process. Written by a person. No money moved.
 *  - `declined`         — refused. Written by a person. Terminal; no money moves.
 *  - `completed`        — the money left. Written by the sync, and only by it.
 *
 * A locally recorded refund therefore cannot reach `completed` on its own, and
 * that is what makes double counting impossible rather than merely unlikely:
 * the request and the movement are never both in the figure that means "money
 * returned". See `REFUND_ADOPTION_WINDOW_DAYS` for how the two are joined up
 * when the movement does arrive.
 */
export const REFUND_STATUSES = [
    'pending_approval',
    'approved',
    'declined',
    'completed',
];
/**
 * Statuses meaning "committed, but the money has not moved".
 *
 * Reported beside the refunded total and never summed into it, for the same
 * reason AR reports `not_yet_due` outside every overdue figure: an intention
 * and a payment are different facts, and one headline covering both is the
 * figure that gets misquoted.
 */
export const OUTSTANDING_REFUND_STATUSES = [
    'pending_approval',
    'approved',
];
/** The statuses a person may set. `completed` is the sync's alone. */
export const REFUND_DECISIONS = ['approved', 'declined'];
export const REFUND_STATUS_META = {
    pending_approval: {
        label: 'Pending approval',
        description: 'Recorded here and waiting on a decision. No money has moved, and none will until somebody approves it.',
        settled: false,
    },
    approved: {
        label: 'Approved',
        description: 'Cleared to process. Still no money moved — this stays here until a matching refund arrives from Clio, which is the only evidence that it did.',
        settled: false,
    },
    declined: {
        label: 'Declined',
        description: 'Refused, with the reason recorded. Terminal: no money moves.',
        settled: false,
    },
    completed: {
        label: 'Completed',
        description: 'The money went back. Evidenced by an upstream record of it leaving, never by this platform alone.',
        settled: true,
    },
};
/**
 * Which account the money came out of (KAN-95).
 *
 * This is the distinction the ticket calls "the sensitive category", and it is
 * a genuinely different kind of event rather than a label:
 *
 *  - A **trust refund** returns *client* money the firm was holding and never
 *    earned. It moves through the IOLTA account, and returning client funds is
 *    a regulated action with bar-rule consequences if it is done wrong.
 *  - A **billing credit** returns money the firm had already earned and
 *    invoiced. It moves through the operating account. Getting it wrong is an
 *    accounting error, not a compliance one.
 *
 * Two values and no `unknown`. A synced refund gets its kind from where the
 * money actually moved — the trust ledger link — and somebody recording one by
 * hand is made to say which it is, because "we are not sure whether this was
 * client money" is not a state a firm should be able to record about a refund.
 */
export const REFUND_KINDS = ['trust', 'billing_credit'];
export const REFUND_KIND_META = {
    trust: {
        label: 'Trust refund',
        short: 'Trust',
        description: 'Unearned client money returned out of the IOLTA account. A regulated action: it is client funds, not the firm’s, and every one carries the trust movement that paid it.',
    },
    billing_credit: {
        label: 'Billing credit',
        short: 'Credit',
        description: 'Money the firm had already earned and invoiced, given back out of the operating account. An accounting correction rather than a trust matter.',
    },
};
/**
 * Approval tiers (KAN-95: "capture who approved, when, and against what
 * threshold").
 *
 * The band is stamped on the refund **at the moment somebody decides**, not
 * computed when the dashboard is read. A threshold that moves later must not
 * retroactively change what tier a past approval was given at — that would make
 * the audit trail describe a policy that did not exist when the decision was
 * made, which is the one thing an approval record has to get right.
 *
 * The boundary below is a **default rather than firm policy**, in exactly the
 * sense `DEFAULT_AR_RISK_RULE` is: one named constant, changed in one place,
 * and echoed back in every response so the figure it produced can be checked.
 * The firm has not yet set its own, and this is written down as needing Finance
 * sign-off rather than presented as settled.
 */
export const REFUND_APPROVAL_TIERS = ['standard', 'elevated'];
/**
 * Where `standard` ends and `elevated` begins, in minor units.
 *
 * $2,500 because it is roughly the point at which a refund at this firm stops
 * being a rounding correction on one bill and starts being a material return of
 * a retainer. A placeholder with a reason, not a measurement.
 */
export const ELEVATED_APPROVAL_THRESHOLD_MINOR = 250_000;
export const REFUND_APPROVAL_TIER_META = {
    standard: {
        label: 'Standard',
        description: 'Below the elevated threshold. One approver.',
    },
    elevated: {
        label: 'Elevated',
        description: 'At or above the elevated threshold, so it warrants a second pair of eyes. Recorded on the refund at the moment it was decided, against the threshold in force then.',
    },
};
export function approvalTierFor(amountMinor, thresholdMinor = ELEVATED_APPROVAL_THRESHOLD_MINOR) {
    return amountMinor >= thresholdMinor ? 'elevated' : 'standard';
}
/**
 * Why the money went back.
 *
 * An enum rather than free text, because KAN-40's goal is to "surface trends to
 * identify recurring issues" and a free-text reason cannot be trended — it can
 * only be read one row at a time, which is what a dashboard exists to avoid.
 * `reasonNote` carries the particulars beside it.
 *
 * `unclassified` is the honest starting state for anything that arrives from
 * Clio: a bank transaction has no reason field, and inferring one from the
 * description text would be keyword-guessing a financial classification. It is
 * counted and named on the dashboard rather than folded into a neighbour, so
 * "fourteen of these are unclassified" is visible instead of silently
 * understating every other reason.
 */
export const REFUND_REASONS = [
    'unearned_retainer',
    'matter_withdrawn',
    'overpayment',
    'duplicate_payment',
    'billing_error',
    'service_credit',
    'unclassified',
];
export const REFUND_REASON_META = {
    unearned_retainer: {
        label: 'Unearned retainer returned',
        description: 'Client money held in trust and never earned, returned when the matter closed. The normal end of an engagement, not a defect.',
        attribution: 'neither',
    },
    matter_withdrawn: {
        label: 'Matter withdrawn',
        description: 'The engagement ended before the work was done. Expected in family law and not, by itself, a sign of anything wrong.',
        attribution: 'neither',
    },
    overpayment: {
        label: 'Overpayment',
        description: 'The client paid more than was owed and the excess went back.',
        attribution: 'client',
    },
    duplicate_payment: {
        label: 'Duplicate payment',
        description: 'The same amount was received twice and one of them was returned.',
        attribution: 'client',
    },
    billing_error: {
        label: 'Billing error',
        description: 'The firm billed something it should not have. The refund is the correction, and the entry that caused it is worth finding.',
        attribution: 'firm',
    },
    service_credit: {
        label: 'Service credit',
        description: 'A concession the firm chose to make. Deliberate, but still a cost the firm caused.',
        attribution: 'firm',
    },
    unclassified: {
        label: 'Not yet classified',
        description: 'Arrived from Clio, which records no reason. Counted and named rather than guessed at from the description — an unclassified refund understates whichever reason it really belongs to.',
        attribution: 'neither',
    },
};
/** The reasons that mean the firm caused the refund. The trend to watch. */
export const FIRM_ATTRIBUTED_REASONS = REFUND_REASONS.filter((reason) => REFUND_REASON_META[reason].attribution === 'firm');
export function isFirmAttributed(reason) {
    return REFUND_REASON_META[reason].attribution === 'firm';
}
/**
 * How long after a refund is approved here a matching upstream movement may be
 * treated as the *same* refund rather than a second one.
 *
 * Thirty days, and the match has to be exact on client and amount and unique
 * within the window — see `services/client_refunds.ts`. This is not fuzzy
 * matching: an ambiguous case, meaning nothing matches or more than one does,
 * falls through to recording the upstream movement as its own refund and
 * leaving the approved request open, which is visible on the dashboard.
 * Guessing here would silently merge two genuine refunds of equal value into
 * one and understate what went back to clients.
 */
export const REFUND_ADOPTION_WINDOW_DAYS = 30;
/** How refund lines are grouped. Each answers a different question. */
export const REFUND_GROUPINGS = [
    'client',
    'attorney',
    'matter_type',
    'reason',
    'matter',
];
/**
 * The groupings KAN-95 asks concentration to be surfaced across.
 *
 * Attorney and matter type, and not client: a client concentration is
 * arithmetic about one relationship and usually says only that one client is
 * large. "Which attorney or matter type keeps generating refunds" is the
 * question the ticket is actually asking, because those are the two that name
 * something the firm can change.
 */
export const CONCENTRATION_GROUPINGS = [
    'attorney',
    'matter_type',
];
export const REFUND_GROUPING_META = {
    client: {
        label: 'Client',
        description: 'Who the money went back to. Every refund belongs to exactly one client.',
    },
    attorney: {
        label: 'Responsible attorney',
        description: 'Who ran the matter the refund came off, via the matter. Refunds with no matter, or a matter with no responsible attorney, appear as one unassigned row.',
    },
    matter_type: {
        label: 'Matter type',
        description: 'The practice area the matter sits in, from Clio. Refunds with no matter, or a matter Clio has not categorised, appear as one uncategorised row.',
    },
    reason: {
        label: 'Reason',
        description: 'Why it went back. The grouping the trend is for — but read the unclassified count first, because those rows belong under one of the others.',
    },
    matter: {
        label: 'Matter',
        description: 'What it came off. Refunds issued at client level appear as one unattributed row.',
    },
};
/**
 * Trend bucket width.
 *
 * Offered rather than inferred from the range: a quarter of data can reasonably
 * be read weekly or monthly, and which one answers the question is the reader's
 * call rather than something to guess from the number of days they picked.
 */
export const REFUND_INTERVALS = ['week', 'month', 'quarter'];
export const REFUND_INTERVAL_META = {
    week: { label: 'Weekly' },
    month: { label: 'Monthly' },
    quarter: { label: 'Quarterly' },
};
/** Default window when a caller names neither end. A year reads as a trend. */
export const REFUND_DEFAULT_WINDOW_MONTHS = 12;
export const DEFAULT_CONCENTRATION_RULE = {
    minShareMultiple: 2,
    minRefundCount: 2,
};
/**
 * Whether one group's share is concentrated under the rule.
 *
 * `groupCount` is how many groups share the total. With one group there is
 * nothing to concentrate *against* — it holds 100% of the refunds because it is
 * the only one — so this is false rather than trivially true.
 */
export function isConcentrated(shareBps, refundCount, groupCount, rule = DEFAULT_CONCENTRATION_RULE) {
    if (groupCount < 2)
        return false;
    if (refundCount < rule.minRefundCount)
        return false;
    const evenShareBps = 10_000 / groupCount;
    return shareBps >= evenShareBps * rule.minShareMultiple;
}
/**
 * The most trend buckets one response will carry.
 *
 * A weekly interval over a decade is a request nobody meant to make, and
 * answering it literally is a slow query and an unreadable chart. The response
 * says which interval it actually used, so a caller can tell it was widened.
 */
export const REFUND_MAX_TREND_POINTS = 60;
/** An empty per-reason breakdown, for accumulating into. */
export function emptyReasonTotals() {
    return Object.fromEntries(REFUND_REASONS.map((reason) => [reason, 0]));
}
/** An empty trust/credit split, for accumulating into. */
export function emptyKindTotals() {
    return { trust: 0, billing_credit: 0 };
}
//# sourceMappingURL=refunds.js.map